import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { RaceTimetableLine } from './RaceTimetableLine';
import { CompetitorInfoLine } from './CompetitorInfoLine';
import { DrawInfoLine } from './DrawInfoLine';
import { Container, NavbarBrand, Navbar } from 'react-bootstrap';
import { useAuth } from './auth/AuthContext';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import Offcanvas from 'react-bootstrap/Offcanvas';
import Card from 'react-bootstrap/Card';
import ListGroup from 'react-bootstrap/ListGroup';
import ReactGA from "react-ga4";
import Spinner from 'react-bootstrap/Spinner';
import '../css/livetimetable.css'
import axios from "axios";
import React from 'react';
import { useCompetition } from './CompetitionContext';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { createRealtimeMapCoalescer, getConnectedUsersCount, invokeRealtime, subscribeToRealtime } from './realtimeHub';

function formatEventTitle(value) {
    const titleCased = String(value || '')
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

    return titleCased
        .replace(/\bmas\s*([a-j](?:\s*\/\s*[a-j])*)\b/gi, (_, category) => `Mas${String(category).replace(/\s+/g, '').toUpperCase()}`)
        .replace(/\bmas([a-j](?:\/[a-j])*)\b/gi, (_, category) => `Mas${String(category).toUpperCase()}`)
        .replace(/\b(i|ii|iii|iv|v|vi|vii|viii|ix|x|xi|xii)\b/gi, token => token.toUpperCase());
}

export function LiveTimetable() {
    const DEFAULT_RESULT_TITLE_FLASH_SECONDS = 20;
    const configuredResultTitleFlashSeconds = Number.parseInt(process.env.REACT_APP_RESULT_TITLE_FLASH_SECONDS || '', 10);
    const resultTitleFlashDurationMs = ((Number.isFinite(configuredResultTitleFlashSeconds) && configuredResultTitleFlashSeconds > 0)
        ? configuredResultTitleFlashSeconds
        : DEFAULT_RESULT_TITLE_FLASH_SECONDS) * 1000;

    const { ReDrawAvailable, currentYear, competitionName } = useCompetition();
    const { isAdmin, isStarter, isFinishTent, getToken, loading: authLoading } = useAuth();
    const [races, setRaces] = useState([]);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState('Disconnected.');
    const [lastUpdate, setLastUpdate] = useState('');
    const [connectedUsers, setConnectedUsers] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogRace, setDialogRace] = useState(null);
    const [timeDialogOpen, setTimeDialogOpen] = useState(false);
    const [timeDialogRace, setTimeDialogRace] = useState(null);
    const [newRaceTime, setNewRaceTime] = useState('');
    const [timeDialogError, setTimeDialogError] = useState('');
    const [timeDialogSaving, setTimeDialogSaving] = useState(false);
    const [canvasOpen, setCanvasOpen] = useState(false);
    const [canvasRace, setCanvasRace] = useState(null);
    const [drawCanvasOpen, setDrawCanvasOpen] = useState(false);
    const [drawCanvasInfo, setDrawCanvasInfo] = useState(null);
    const [printTitle, setPrintTitle] = useState(null);
    const [verdict, setVerdict] = useState("");
    const [winningTime, setWinningTime] = useState("");
    const [selectWinner, setSelectWinner] = useState("");
    const [drawButtonLoading, setDrawButtonLoading] = useState(false);
    const isMountedRef = useRef(false);
    const titleFlashIntervalRef = useRef(null);
    const titleFlashTimeoutRef = useRef(null);
    const titleFlashBaseTitleRef = useRef('Regatta');
    const displayYear = currentYear || new Date().getFullYear();

    const getDefaultTabTitle = useCallback(() => {
        const heading = document.getElementById('tabelLabel');
        const headingText = heading?.textContent?.trim();
        if (headingText) return headingText;
        if (competitionName) return `${competitionName} - Live Timetable`;
        return `${printTitle || ''} ${displayYear} Live Timetable`.trim() || 'Regatta';
    }, [competitionName, displayYear, printTitle]);

    const formatLaneLabel = useCallback((lane) => {
        const club = String(lane?.titleAbbreviated || lane?.titleMobile || lane?.titleFull || lane?.title || lane?.titleShort || '').trim();
        const competitorNo = String(lane?.competitorNo || '').trim();
        if (!club) return 'Unknown crew';
        if (!competitorNo) return club;
        if (/^(TBC|BYE)$/i.test(club)) return club;
        if (club.startsWith(`${competitorNo} - `)) return club;
        return `${competitorNo} - ${club}`;
    }, []);

    const showMastersCategoryByEventId = useMemo(() => {
        const categoriesByEvent = new Map();

        races.forEach(race => {
            const eventId = race?.event?.id;
            if (!eventId || eventId < 0) return;
            if (!race?.event?.handicapEvent) return;

            if (!categoriesByEvent.has(eventId)) {
                categoriesByEvent.set(eventId, new Set());
            }

            const categories = categoriesByEvent.get(eventId);
            const laneA = String(race?.laneA?.mastersCategory || '').trim().toUpperCase().replace(/\s+/g, '');
            const laneB = String(race?.laneB?.mastersCategory || '').trim().toUpperCase().replace(/\s+/g, '');
            if (laneA) categories.add(laneA);
            if (laneB) categories.add(laneB);
        });

        const flags = new Map();
        categoriesByEvent.forEach((categories, eventId) => {
            flags.set(eventId, categories.size > 1);
        });
        return flags;
    }, [races]);

    const clearResultTitleFlash = useCallback(() => {
        if (titleFlashIntervalRef.current) {
            window.clearInterval(titleFlashIntervalRef.current);
            titleFlashIntervalRef.current = null;
        }
        if (titleFlashTimeoutRef.current) {
            window.clearTimeout(titleFlashTimeoutRef.current);
            titleFlashTimeoutRef.current = null;
        }
        if (titleFlashBaseTitleRef.current) {
            document.title = titleFlashBaseTitleRef.current;
        }
    }, []);

    const flashResultTitle = useCallback((message) => {
        const alertTitle = String(message || '').trim();
        if (!alertTitle) return;

        titleFlashBaseTitleRef.current = getDefaultTabTitle();
        clearResultTitleFlash();
        const baseTitle = titleFlashBaseTitleRef.current || 'Regatta';

        let showAlertTitle = true;
        document.title = alertTitle;

        titleFlashIntervalRef.current = window.setInterval(() => {
            document.title = showAlertTitle ? baseTitle : alertTitle;
            showAlertTitle = !showAlertTitle;
        }, 1000);

        titleFlashTimeoutRef.current = window.setTimeout(() => {
            clearResultTitleFlash();
        }, resultTitleFlashDurationMs);
    }, [clearResultTitleFlash, getDefaultTabTitle, resultTitleFlashDurationMs]);

    useEffect(() => {
        isMountedRef.current = true;
        titleFlashBaseTitleRef.current = getDefaultTabTitle();
        return () => {
            isMountedRef.current = false;
            clearResultTitleFlash();
        };
    }, [clearResultTitleFlash, getDefaultTabTitle]);

    useEffect(() => {
        const style = document.createElement('style');
        style.setAttribute('data-print-page', 'timetable-landscape');
        style.textContent = '@media print { @page { size: A4 landscape !important; margin: 1.5cm !important; } }';
        document.head.appendChild(style);

        return () => {
            style.remove();
        };
    }, []);

    const populateRaceData = useCallback(async () => {
        try {
            const token = getToken();
            const response = await fetch('/api/timetable', {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
            const actualData = await response.json();
            if (!isMountedRef.current) return;
            setRaces(actualData);
            setLoading(false);
            const meetingResponse = await fetch('/api/meeting/current', {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            if (meetingResponse.ok) {
                const actualMeeting = await meetingResponse.json();
                var parts = actualMeeting.meetingDate.split("/");
                var dt = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10)-1, parseInt(parts[0], 10));
                if (!isMountedRef.current) return;
                setPrintTitle(dt.toLocaleString('en-us', { weekday: 'long' }));
            }
        } catch (err) {
            console.log(err.message);
        }
    }, [getToken]);

    useEffect(() => {
        if (authLoading) {
            return undefined;
        }

        populateRaceData();

        const receiveRaceResultCoalescer = createRealtimeMapCoalescer((events) => {
            setRaces(prevRaces => {
                const _races = [...prevRaces];
                let lastRaceUpdate = null;

                events.forEach(({ competitionId, raceid, winnerid, verdict, timetaken, nextrace }) => {
                    let raceUpdate = _races.find(race => race.raceId === raceid && race.competitionId === competitionId);
                    if (!raceUpdate) return;
                    let winnerInfo, loserinfo;
                    raceUpdate.verdict = verdict;
                    raceUpdate.winningTime = timetaken;
                    raceUpdate.laneA.winner = false;
                    raceUpdate.laneB.winner = false;
                    if (winnerid === 'a') {
                        raceUpdate.laneA.winner = true;
                        winnerInfo = raceUpdate.laneA;
                        loserinfo = raceUpdate.laneB;
                    }
                    if (winnerid === 'b') {
                        raceUpdate.laneB.winner = true;
                        winnerInfo = raceUpdate.laneB;
                        loserinfo = raceUpdate.laneA;
                    }
                    if (raceUpdate.laneA.winner || raceUpdate.laneB.winner) {
                        raceUpdate.status = 3;
                    }
                    if ((raceUpdate.round !== 'F' || nextrace?.raceId != null) && winnerInfo) {
                        let nextRound = _races.find(race => race.raceId === nextrace?.raceId);
                        if (nextRound) {
                            if (nextRound.wora === raceUpdate.raceId) {
                                nextRound.laneA.title = winnerInfo.title;
                                nextRound.laneA.titleMobile = winnerInfo.titleMobile;
                                nextRound.laneA.titleAbbreviated = winnerInfo.titleAbbreviated;
                                nextRound.laneA.titleFull = winnerInfo.titleFull;
                                nextRound.laneA.competitorNo = winnerInfo.competitorNo;
                                nextRound.laneA.suffix = winnerInfo.suffix;
                                nextRound.laneA.titleCode = winnerInfo.titleCode;
                            }
                            if (nextRound.worb === raceUpdate.raceId) {
                                nextRound.laneB.title = winnerInfo.title;
                                nextRound.laneB.titleMobile = winnerInfo.titleMobile;
                                nextRound.laneB.titleAbbreviated = winnerInfo.titleAbbreviated;
                                nextRound.laneB.titleFull = winnerInfo.titleFull;
                                nextRound.laneB.competitorNo = winnerInfo.competitorNo;
                                nextRound.laneB.suffix = winnerInfo.suffix;
                                nextRound.laneB.titleCode = winnerInfo.titleCode;
                            }
                        }
                        let plateRound = _races.find(race => (race.wora === raceid && race.lora) || (race.worb === raceid && race.lorb));
                        if (plateRound && loserinfo) {
                            if (plateRound.wora === raceid) {
                                plateRound.laneA.title = loserinfo.title;
                                plateRound.laneA.titleAbbreviated = loserinfo.titleAbbreviated;
                                plateRound.laneA.titleFull = loserinfo.titleFull;
                                plateRound.laneA.suffix = loserinfo.suffix;
                                plateRound.laneA.titleCode = loserinfo.titleCode;
                            }
                            if (plateRound.worb === raceid) {
                                plateRound.laneB.title = loserinfo.title;
                                plateRound.laneB.titleAbbreviated = loserinfo.titleAbbreviated;
                                plateRound.laneB.titleFull = loserinfo.titleFull;
                                plateRound.laneB.suffix = loserinfo.suffix;
                                plateRound.laneB.titleCode = loserinfo.titleCode;
                            }
                        }
                    }

                    lastRaceUpdate = raceUpdate;
                });

                if (lastRaceUpdate) {
                    const racesCompleted = _races.filter(race => race.laneA.winner || race.laneB.winner);
                    const racesActual = _races.filter(race => race.event.id > 0);
                    let d = new Date().toLocaleTimeString('en-GB', { hour12: false }) + ' (R' + lastRaceUpdate.raceNo + ' - ' + lastRaceUpdate.raceTimeFormatted + ' - ' + (racesCompleted.length / racesActual.length * 100).toFixed(1) + '%)';
                    setLastUpdate(d);

                    const winnerTitle = lastRaceUpdate.laneA?.winner
                        ? (lastRaceUpdate.laneA?.titleShort || lastRaceUpdate.laneA?.title || '')
                        : (lastRaceUpdate.laneB?.titleShort || lastRaceUpdate.laneB?.title || '');
                    const winnerLabel = String(winnerTitle).trim() || 'Race winner';
                    flashResultTitle(`${winnerLabel} won race ${lastRaceUpdate.raceNo}`);
                }

                return _races;
            });
        }, (event) => `${event.competitionId}:${event.raceid}`);

        const raceStatusUpdatedCoalescer = createRealtimeMapCoalescer((events) => {
            setRaces(prevRaces => {
                const _races = [...prevRaces];
                events.forEach(({ competitionId, raceId, statusId }) => {
                    let raceUpdate = _races.find(race => race.raceId === raceId && race.competitionId === competitionId);
                    if (raceUpdate) {
                        raceUpdate.status = statusId;
                    }
                });
                return _races;
            });
        }, (event) => `${event.competitionId}:${event.raceId}`);

        const raceMovedCoalescer = createRealtimeMapCoalescer((events) => {
            setRaces(prevRaces => {
                const _races = [...prevRaces];
                events.forEach(({ competitionId, raceid, newraceid }) => {
                    if (_races.length === 0 || _races[0].competitionId !== competitionId) return;
                    const newRaceId = parseInt(newraceid, 10);
                    const raceA = Object.assign({}, _races.find(race => race.raceId === raceid));
                    const raceB = Object.assign({}, _races.find(race => race.raceId === newRaceId));
                    let destinationRace = _races.find(race => race.raceId === newRaceId);
                    let sourceRace = _races.find(race => race.raceId === raceid);
                    if (!destinationRace || !sourceRace || !raceA || !raceB) return;
                    destinationRace.raceNo = raceA.raceNo;
                    destinationRace.laneA.title = raceA.laneA.title;
                    destinationRace.laneA.titleShort = raceA.laneA.titleShort;
                    destinationRace.laneA.suffix = raceA.laneA.suffix;
                    destinationRace.laneB.title = raceA.laneB.title;
                    destinationRace.laneB.titleShort = raceA.laneB.titleShort;
                    destinationRace.laneB.suffix = raceA.laneB.suffix;
                    destinationRace.event.id = raceA.event.id;
                    destinationRace.event.title = raceA.event.title;
                    destinationRace.event.titleShort = raceA.event.titleShort;
                    destinationRace.round = raceA.round;
                    destinationRace.divsion = raceA.division;
                    destinationRace.nextRaceId = raceA.nextRaceId;
                    sourceRace.raceNo = raceB.raceNo;
                    sourceRace.laneA.title = '';
                    sourceRace.laneB.title = '';
                    sourceRace.laneA.suffix = '';
                    sourceRace.laneB.suffix = '';
                    sourceRace.event.id = -1;
                    sourceRace.event.title = '';
                    sourceRace.event.titleShort = '';
                    sourceRace.round = '';
                    sourceRace.nextRaceId = '';
                });
                return _races;
            });
        }, (event) => `${event.competitionId}:${event.raceid}`);

        const unsubscribe = subscribeToRealtime({
            onStatusChange: (nextStatus, updateTime) => {
                if (!isMountedRef.current) return;
                setStatus(nextStatus);
                setLastUpdate(updateTime);
                if (isAdmin && (nextStatus === 'Connected!' || nextStatus === 'Reconnected!')) {
                    getConnectedUsersCount().then(count => {
                        if (!isMountedRef.current) return;
                        setConnectedUsers(count);
                    });
                }
            },
            onReconnected: () => {
                populateRaceData();
            },
            onConnectedUsersCountUpdated: (count) => {
                if (!isMountedRef.current) return;
                if (!isAdmin) return;
                const parsed = Number(count);
                setConnectedUsers(Number.isFinite(parsed) ? parsed : null);
            },
            onReceiveRaceResult: (competitionId, raceid, winnerid, verdict, timetaken, nextrace) => {
                receiveRaceResultCoalescer.push({ competitionId, raceid, winnerid, verdict, timetaken, nextrace });
            },
            onRaceStatusUpdated: (competitionId, raceId, statusId) => {
                raceStatusUpdatedCoalescer.push({ competitionId, raceId, statusId });
            },
            onRaceMoved: (competitionId, raceid, newraceid) => {
                raceMovedCoalescer.push({ competitionId, raceid, newraceid });
            }
        });
        return () => {
            receiveRaceResultCoalescer.dispose();
            raceStatusUpdatedCoalescer.dispose();
            raceMovedCoalescer.dispose();
            unsubscribe();
        };
    }, [authLoading, isAdmin, populateRaceData, flashResultTitle]);

    useEffect(() => {
        if (!isAdmin) {
            setConnectedUsers(null);
            return undefined;
        }

        let mounted = true;
        const loadCount = async () => {
            const count = await getConnectedUsersCount();
            if (mounted) setConnectedUsers(count);
        };

        loadCount();
        const intervalId = window.setInterval(loadCount, 15000);

        return () => {
            mounted = false;
            window.clearInterval(intervalId);
        };
    }, [isAdmin]);

    async function hideCanvas() {
        setCanvasOpen(false);
        setCanvasRace('');
        setDrawCanvasOpen(false);
        setDrawCanvasInfo(null);
    }

    async function openCanvas(e) {
        ReactGA.event({
            category: "Canvas",
            action: "Competitor Canvas Opened",
            value: 99,
            nonInteraction: false
        });
        if (e === null) return;
        if (!isAdmin) return;
        const raceForCanvas = races.find(race => race.raceId === e);
        let crews = { race: raceForCanvas, laneACompetitors: [], laneBCompetitors: [] };
        const token = getToken();
        if (raceForCanvas.laneA && raceForCanvas.laneA.id) {
            await fetch('/api/competitor/' + raceForCanvas.laneA.id, {
                method: 'GET', credentials: 'include', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            })
                .then(response => response.json())
                .then(data => { crews.laneACompetitors = data; })
                .catch((error) => { console.error('Error loading competitors:', error); });
        }
        if (raceForCanvas.laneB && raceForCanvas.laneB.id) {
            await fetch('/api/competitor/' + raceForCanvas.laneB.id, {
                method: 'GET', credentials: 'include', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            })
                .then(response => response.json())
                .then(data => { crews.laneBCompetitors = data; })
                .catch((error) => { console.error('Error loading competitors:', error); });
        }
        setCanvasOpen(true);
        setCanvasRace(crews);
    }

    async function openDrawCanvas(e) {
        ReactGA.event({
            category: "Canvas",
            action: "Draw Canvas Opened",
            value: 99,
            nonInteraction: false
        });
        const raceClicked = races.find(race => race.raceId === e);
        const racesInEvent = races.filter(race => race.event.id === raceClicked.event.id);
        let event = { event: raceClicked.event.title, eventId: raceClicked.event.id, races: racesInEvent };
        setDrawCanvasOpen(true);
        setDrawCanvasInfo(event);
    }

    async function handleRaceAlign(e) {
        let _races = races;
        let raceUpdate = _races.find(race => { return race.raceId === e; })
        const token = getToken();
        const data = { "raceId": e };
        await fetch('/api/race/alignrace', {
            method: 'POST', credentials: 'include', body: JSON.stringify(data), headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        }).then((response) => {
            if (!response.ok) {
                throw new Error(
                    `This is an HTTP error: The status is ${response.status}`
                );
            }
            invokeRealtime("RaceStatusUpdated", raceUpdate.competitionId, e, 1).catch(function (err) {
                return console.error(err.toString());
            });
            ReactGA.event({
                category: "Race Management",
                action: "Align Competitors for Race",
                value: 99,
                nonInteraction: false
            });
        })
            .catch((err) => {
                alert(err.message);
                console.log(err.message);
            });  
    }
    async function handleRaceStart(e) {
        let _races = races;
        let raceUpdate = _races.find(race => { return race.raceId === e; })
        const token = getToken();
        const data = { "raceId": e };
        await fetch('/api/race/startrace', {
            method: 'POST', credentials: 'include', body: JSON.stringify(data), headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        }).then((response) => {
            if (!response.ok) {
                throw new Error(
                    `This is an HTTP error: The status is ${response.status}`
                );
            }
            invokeRealtime("RaceStatusUpdated", raceUpdate.competitionId, e, 2).catch(function (err) {
                return console.error(err.toString());
            });
            ReactGA.event({
                category: "Race Management",
                action: "Go",
                value: 99,
                nonInteraction: false
            });   
        })
            .catch((err) => {
                alert(err.message);
                console.log(err.message);
            });

    }

    async function handleRaceStop(e) {
        let _races = races;
        let raceUpdate = _races.find(race => { return race.raceId === e; })
        if (window.confirm("Race " + raceUpdate.raceNo + "\n\n" + raceUpdate.laneA.title + " v. " + raceUpdate.laneB.title + "\n\nClick OK cancel the timing of this race")) {
            const token = getToken();
            const data = { "raceId": e };
            await fetch('/api/race/stoprace', {
                method: 'POST', credentials: 'include', body: JSON.stringify(data), headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            }).then((response) => {
                if (!response.ok) {
                    throw new Error(
                        `This is an HTTP error: The status is ${response.status}`
                    );
                } 
                invokeRealtime("RaceStatusUpdated", raceUpdate.competitionId, e, 0).catch(function (err) {
                    return console.error(err.toString());
                });
                ReactGA.event({
                    category: "Race Management",
                    action: "Cancel Race Start",
                    value: 99,
                    nonInteraction: false
                }); 
            })
            .catch((err) => {
                alert(err.message);
                console.log(err.message);
            });
        }
    }

    async function recordDeadHeat(e) {

        const token = getToken();
        const data = { "raceId": dialogRace.raceId };
        await fetch('/api/race/deadheat', {
            method: 'DELETE', credentials: 'include', body: JSON.stringify(data), headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        }).then((response) => {
            if (!response.ok) {
                throw new Error(
                    `This is an HTTP error: The status is ${response.status}`
                );
            } console.log(dialogRace.raceId);
            invokeRealtime("RaceStatusUpdated", dialogRace.competitionId, dialogRace.raceId, 0).catch(function (err) {
                return console.error(err.toString());
            });
            setDialogOpen(false);
            ReactGA.event({
                category: "Race Management",
                action: "Dead Heat",
                value: 99,
                nonInteraction: false
            }); 

        })
        .catch((err) => {
            alert(err.message);
            console.log(err.message);
        });
    }

    async function openResultDialog(e) {

        const race = races.find(race => { return race.raceId === e; });
        let lane = '';
        if (race.laneA.winner)
            lane = 'a';
        if (race.laneB.winner)
            lane = 'b';

        const token = getToken();
        await fetch('/api/timetable/' + e, {
            method: 'GET', credentials: 'include', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        })
            .then(response => { return response.json(); })
            .then(data => {
                race.winningTime = data.winningTime;

                ReactGA.event({
                    category: "Race Management",
                    action: "Open Race Dialog",
                    value: 99,
                    nonInteraction: false
                });  
            })
            .catch((error) => {
                console.error('Error loading race details (to pre-populate the winning time):', error);
            });

        setDialogOpen(true);
        setDialogRace(race);
        setVerdict(race.verdict);
        setWinningTime(race.winningTime);
        setSelectWinner(lane);

    }

    async function cancelResultDialog(e) {

        setDialogOpen(false);
    }

    async function onInputchange(event) {

        if (event.target.name === 'verdict' && (event.target.value.toUpperCase() === 'DNS' || event.target.value.toUpperCase() === 'DNF' || event.target.value.toUpperCase() === 'DSQ' || event.target.value.toUpperCase() === 'NRO'))
            setWinningTime('ntt');

        switch (event.target.name) {
            case 'verdict':
                setVerdict(event.target.value);
                break;
            case 'winningTime':
                setWinningTime(event.target.value);
                break;
            case 'selectWinner':
                setSelectWinner(event.target.value);
                break;
            default:
                break;
        }
    }

    async function saveResultDialog(e) {

        if (!selectWinner || selectWinner===null || selectWinner === '') {
            alert('Please select a winner.');
            return;
        }
        if (winningTime === '') {
            alert('Please enter the time the winner completed the course. If no time was taken, enter NTT.');
            return;
        }
        if (verdict === '') {
            alert('Please enter the verdict (e.g. 2l, easily, nro, scr)');
            return;
        }

        var inputData = { raceId: dialogRace.raceId, verdict: verdict, timeTaken: winningTime, winningLane: selectWinner };

        const token = getToken();
        fetch('/api/timetable/saveraceresult', {
            method: 'POST', credentials: 'include', body: JSON.stringify(inputData), headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        })
            .then(response => { return response.json(); })
            .then(data => {
                if (data.status === 'Ok') {
                    var nextRace = {
                        raceId: data.race.nextRaceId, winner: data.race.winningCompetitor
                    };
                    invokeRealtime("BroadcastRaceResult", dialogRace.competitionId, dialogRace.raceId, selectWinner, verdict, winningTime, nextRace).catch(function (err) {
                        return console.error(err.toString());
                    });
                }
            })
            .catch((error) => {
                alert('Error submitting race result.');
                console.error('Error:', error);
            });

        setDialogOpen(false);

        ReactGA.event({
            category: "Save Close Race Result",
            action: "Go",
            value: 99,
            nonInteraction: false
        });  
    }

    async function endRaceTimer(e) {

        const data = { 'raceId': dialogRace.raceId };
        const token = getToken();
        await fetch('/api/race/completerace', {
            method: 'POST', credentials: 'include', body: JSON.stringify(data), headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(
                        `This is an HTTP error: The status is ${response.status}`
                    );
                }
                return response.json();
            })
            .then((data) => {

                invokeRealtime("RaceStatusUpdated", dialogRace.competitionId, dialogRace.raceId, 3).catch(function (err) {
                    return console.error(err.toString());
                });

                setWinningTime(data.timeTaken);
            })
            .catch((err) => {
                alert(err);
                console.log(err.message);
            });

        ReactGA.event({
            category: "Stop Race Timer",
            action: "Go",
            value: 99,
            nonInteraction: false
        });  
    }

    async function moveRace(e) {

        var dest = prompt("Please enter which spare race to move this to:", "");
        if (dest === null || dest === "") {
            return;
        }

        const inputData = { raceId: e, newRaceNo: dest };
        const token = getToken();
        try {
            const response = await fetch('/api/race/moverace', {
                method: 'POST', credentials: 'include', body: JSON.stringify(inputData), headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });

            const payload = await response.json().catch(() => ({}));
            if (!response.ok || payload?.status !== 'Ok') {
                const reason = payload?.message || `Failed to move race (HTTP ${response.status}).`;
                throw new Error(reason);
            }

            invokeRealtime("MoveRace", payload.competitionId, inputData.raceId, payload.newRaceId).catch(function (err) {
                return console.error(err.toString());
            });

            ReactGA.event({
                category: "Move Race",
                action: "Go",
                value: 99,
                nonInteraction: false
            });

            alert(payload.message);
        } catch (error) {
            alert('Error moving race: ' + (error?.message || String(error)));
            console.error('Error:', error);
        }

 
    }

    function getRaceTimeMinutes(race) {
        const match = String(race?.raceTimeFormatted || '').trim().match(/^(\d{1,2}):(\d{2})$/);
        if (!match) return null;
        const hours = Number(match[1]);
        const minutes = Number(match[2]);
        if (hours > 23 || minutes > 59) return null;
        return hours * 60 + minutes;
    }

    function openChangeTimeDialog(e) {
        const race = races.find(race => race.raceId === e);
        if (!race) return;
        setTimeDialogRace(race);
        setNewRaceTime(String(race.raceTimeFormatted || '').trim());
        setTimeDialogError('');
        setTimeDialogOpen(true);
    }

    function cancelChangeTimeDialog() {
        setTimeDialogOpen(false);
        setTimeDialogRace(null);
        setTimeDialogError('');
    }

    async function saveChangeTimeDialog() {
        const timeValue = String(newRaceTime || '').trim();
        const match = timeValue.match(/^(\d{1,2}):(\d{2})$/);
        const hours = match ? Number(match[1]) : NaN;
        const minutes = match ? Number(match[2]) : NaN;
        if (!match || hours > 23 || minutes > 59) {
            setTimeDialogError('Please enter a valid time in HH:mm (24-hour) format.');
            return;
        }

        const newMinutes = hours * 60 + minutes;
        const scheduledMinutes = races.map(getRaceTimeMinutes).filter(value => value !== null);
        if (scheduledMinutes.length) {
            const formatMinutes = (total) => `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
            const timetableStart = Math.min(...scheduledMinutes);
            const timetableEnd = Math.max(...scheduledMinutes);
            if (newMinutes < timetableStart - 60) {
                setTimeDialogError(`New time cannot be more than one hour before the timetable start (${formatMinutes(timetableStart)}).`);
                return;
            }
            if (newMinutes > timetableEnd + 60) {
                setTimeDialogError(`New time cannot be more than one hour after the timetable end (${formatMinutes(timetableEnd)}).`);
                return;
            }
        }

        setTimeDialogError('');
        setTimeDialogSaving(true);
        const inputData = { raceId: timeDialogRace.raceId, newTime: timeValue };
        const token = getToken();
        try {
            const response = await fetch('/api/race/changetime', {
                method: 'POST', credentials: 'include', body: JSON.stringify(inputData), headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });

            const payload = await response.json().catch(() => ({}));
            if (!response.ok || payload?.status !== 'Ok') {
                const reason = payload?.message || `Failed to change race time (HTTP ${response.status}).`;
                throw new Error(reason);
            }

            await populateRaceData();

            ReactGA.event({
                category: "Change Race Time",
                action: "Go",
                value: 99,
                nonInteraction: false
            });

            setTimeDialogOpen(false);
            setTimeDialogRace(null);
        } catch (error) {
            setTimeDialogError(error?.message || String(error));
            console.error('Error:', error);
        } finally {
            setTimeDialogSaving(false);
        }
    }

    async function handleDrawAdminAction() {
        setDrawButtonLoading(true);
        try {
            await axios.put(`/api/draw/${drawCanvasInfo.eventId}`);
            // Reload races and keep draw canvas open with current event
            await populateRaceData();
            // Optionally, re-fetch drawCanvasInfo to refresh event data
            if (drawCanvasInfo && drawCanvasInfo.eventId) {
                const raceClicked = races.find(race => race.event.id === drawCanvasInfo.eventId);
                const racesInEvent = races.filter(race => race.event.id === drawCanvasInfo.eventId);
                let event = { event: raceClicked ? raceClicked.event.title : drawCanvasInfo.event, eventId: drawCanvasInfo.eventId, races: racesInEvent };
                setDrawCanvasInfo(event);
            }
        } catch (err) {
            alert('Error updating event.');
        }
        setDrawButtonLoading(false);
    }

    const handleExport = () => {
        const formatExportRaceTime = (rawValue) => {
            const value = String(rawValue || '').trim();
            if (!value) return '';

            const hhmmss = value.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
            if (hhmmss) {
                const hh = String(Number(hhmmss[1] || 0)).padStart(2, '0');
                const mm = hhmmss[2];
                return `${hh}:${mm}`;
            }

            const parsed = new Date(value);
            if (Number.isNaN(parsed.getTime())) return value;

            const hh = String(parsed.getHours()).padStart(2, '0');
            const mm = String(parsed.getMinutes()).padStart(2, '0');
            return `${hh}:${mm}`;
        };

        const formatLaneForExport = (race, lane) => {
            const title = String(lane?.title || '').trim();
            const crew = String(lane?.suffix || '').trim();
            const mastersCategory = String(lane?.mastersCategory || '').trim().toUpperCase();
            const categoryTag = race?.event?.handicapEvent && mastersCategory ? ` [Mas${mastersCategory}]` : '';

            if (crew) return `${title} (${crew})${categoryTag}`.trim();
            return `${title}${categoryTag}`.trim();
        };

        const header = ['No', 'Next', 'Time', 'Event Title', 'Round', 'Enclosure (Middlesex) Station', 'Surrey Station'];
        const data = races.map(race => ([
            race.raceNo,
            race.nextRaceNo,
            formatExportRaceTime(race?.raceTimeFormatted || race?.raceTime || ''),
            race.event.title,
            race.round,
            formatLaneForExport(race, race.laneA),
            formatLaneForExport(race, race.laneB)
        ]));

        const worksheetData = [header, ...data];
        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

        // Set column widths
        worksheet['!cols'] = [
            { wch: 5 }, // No
            { wch: 5 }, // Next
            { wch: 8 }, // Time
            { wch: 30 }, // Event Title
            { wch: 8 }, // Round
            { wch: 50 }, // Enclosure (Middlesex) Station
            { wch: 50 }  // Surrey Station
        ];

        // Make header bold
        const headerRange = XLSX.utils.decode_range(worksheet['!ref']);
        for (let C = headerRange.s.c; C <= headerRange.e.c; ++C) {
            const cellRef = XLSX.utils.encode_cell({ c: C, r: 0 });
            if (worksheet[cellRef]) {
                worksheet[cellRef].s = { font: { bold: true } };
            }
        }

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Timetable');
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        saveAs(blob, 'timetable.xlsx');
    };

    function renderRaceTimetable(races) {
        let adjuster = 0;
        if (!isAdmin && !isStarter && !isFinishTent) adjuster = 1;
        const hasSpareRaces = races.some(race => race?.event?.id === -1);
        return (
            <div className='container-fluid race-timetable'>
                <div className='row rowheader'>
                    <Col md={5}>
                        <Row>
                            <div className='col-1 order-first'>No</div>
                            <div className='col-1 order-first d-none d-lg-inline'>Next</div>
                            <div className='col-9 col-lg-8'>Event</div>
                            <div className='col-2 order-md-first order-last text-end text-md-start'>Time</div>
                        </Row>
                    </Col>
                    <Col md={adjuster ? 7 : 5}>
                        <Row>
                            <div className='col-6 crew-name'>Middlesex (Enclosure)</div>
                            <div className='col-6 crew-name'>Surrey</div>
                        </Row>
                    </Col>
                </div>
                {races.map((race) => (<RaceTimetableLine key={race.raceId} value={race} showMastersCategory={Boolean(showMastersCategoryByEventId.get(race?.event?.id))} changeA={handleRaceStart} changeB={handleRaceStop} showStart={isStarter} showStop={isStarter} showMove={isAdmin} showResult={isFinishTent} changeC={openResultDialog} changeD={moveRace} changeE={openChangeTimeDialog} changeZ={handleRaceAlign} showCanvas={openCanvas} showDrawCanvas={openDrawCanvas} hasSpareRaces={hasSpareRaces} />))}
            </div>
        );
    }

    let contents = loading ? <p><em>Loading...</em></p> : renderRaceTimetable(races);
    return (
        <div className="pb-5 mb-5">
            <Offcanvas show={drawCanvasOpen} onHide={hideCanvas} placement="end">
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>Draw for {drawCanvasInfo && formatEventTitle(drawCanvasInfo.event)}</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    {drawCanvasInfo && drawCanvasInfo.races.map((race) => (<DrawInfoLine key={race.raceId} value={race} />))}
                    <br />
                    <span className='crew-name'>{isAdmin && drawCanvasInfo && drawCanvasInfo.eventId}</span>
                </Offcanvas.Body>
                {isAdmin && drawCanvasInfo && drawCanvasInfo.eventId && ReDrawAvailable && (
                    <div className="p-3 border-top bg-light text-end">
                        <Button variant="primary" onClick={handleDrawAdminAction} disabled={drawButtonLoading}>
                            {drawButtonLoading ? <Spinner size="sm" animation="border" /> : 'Redraw Event'}
                        </Button>
                    </div>
                )}
            </Offcanvas>
            <Offcanvas show={canvasOpen} onHide={hideCanvas} className='tt-crew-offcanvas'>
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>Competitors in Race {canvasRace && canvasRace.race.raceNo}</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    {canvasRace && <div className="alert alert-primary" role="alert">{formatEventTitle(canvasRace.race.event.title)} ({canvasRace.race.round})</div>}
                    <>
                        <Card>
                            <Card.Header>{canvasRace && canvasRace.race.laneA.title} ({canvasRace && canvasRace.race.laneA.id})</Card.Header>
                            <ListGroup variant="flush">
                                {canvasRace && canvasRace.laneACompetitors.map((competitor, index) => (<CompetitorInfoLine key={`${competitor.membershipNo || 'm'}-${competitor.position || 'p'}-${index}`} value={competitor} />))}
                            </ListGroup>
                        </Card>
                        <Card>
                            <Card.Header>{canvasRace && canvasRace.race.laneB.title} ({canvasRace && canvasRace.race.laneB.id})</Card.Header>
                            <ListGroup variant="flush">
                                {canvasRace && canvasRace.laneBCompetitors.map((competitor, index) => (<CompetitorInfoLine key={`${competitor.membershipNo || 'm'}-${competitor.position || 'p'}-${index}`} value={competitor} />))}
                            </ListGroup>
                        </Card>
                    </>
                </Offcanvas.Body>
            </Offcanvas>
            <Modal show={dialogOpen} onHide={cancelResultDialog} backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>Submit Race Result for Race {dialogRace && dialogRace.raceNo}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Container>

                    {dialogRace && dialogRace.status === 2 &&
                            <><Row>
                            <Col className='text-center'><Alert key='warning' variant='warning'>Race Underway - Stopwatch Running!</Alert></Col>
                        </Row>
                        <Row>
                                <Col className='text-center'>{dialogRace && formatLaneLabel(dialogRace.laneA)} v {dialogRace && formatLaneLabel(dialogRace.laneB)}</Col>
                        </Row></>
                    }
                        {dialogRace && dialogRace.status !== 2 &&
                         <>
                                <Form.Group className="mb-3" controlId="exampleForm.selectWinner">
                                    <Form.Label>Select a Winner</Form.Label>
                                <Form.Select name='selectWinner' onChange={onInputchange} value={selectWinner} defaultValue={''}>
                                    <option value='' disabled></option>
                                    <option value='a'>{formatLaneLabel(dialogRace.laneA)}</option>
                                    <option value='b'>{formatLaneLabel(dialogRace.laneB)}</option>
                                    </Form.Select>
                                    </Form.Group>
                            <Row className="mb-3">
                                <Form.Group as={Col} controlId="exampleForm.verdict">
                                    <Form.Label>Verdict</Form.Label>
                                    <Form.Control type='text' placeholder="4l, nro, easily" name='verdict' value={verdict} onChange={onInputchange} aria-describedby="vHelpBlock" />
                                    <Form.Text id="vHelpBlock" muted>e.g. 4l, nro, easily, scr, dns, dnf, dsq</Form.Text>
                                </Form.Group>
                                    <Form.Group as={Col} controlId="exampleForm.winningTime">
                                        <Form.Label>Winning Time</Form.Label>
                                    <Form.Control type='text' name='winningTime' value={winningTime} onChange={onInputchange} maxLength='5' aria-describedby="wtHelpBlock" />
                                    <Form.Text id="wtHelpBlock" muted>e.g. 3:02, ntt</Form.Text>
                                    </Form.Group>
                                </Row>
                            </>
                    }
                    </Container>

                </Modal.Body>
                <Modal.Footer>
                    {dialogRace && dialogRace.status === 2 &&
                        <Button variant="primary" onClick={endRaceTimer}>
                            End Race Timer
                        </Button>
                    }
                    {dialogRace && dialogRace.status !== 2 &&
                        <Container><Row><Col><Button variant="warning" onClick={recordDeadHeat}>
                            Dead Heat!
                        </Button></Col>
                            <Col className="text-end"><Button variant="secondary" onClick={cancelResultDialog}>
                        Cancel
                    </Button>&nbsp;
                    <Button variant="primary" onClick={saveResultDialog}>
                        Save Result
                    </Button></Col></Row></Container>}
                </Modal.Footer>
            </Modal>
            <Modal show={timeDialogOpen} onHide={cancelChangeTimeDialog} backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>Change Time for Race {timeDialogRace && timeDialogRace.raceNo}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Container>
                        {timeDialogError && <Alert variant="danger">{timeDialogError}</Alert>}
                        <Form.Group className="mb-3" controlId="changeTimeForm.newRaceTime">
                            <Form.Label>New Race Time</Form.Label>
                            <Form.Control type="time" name="newRaceTime" value={newRaceTime} onChange={(event) => setNewRaceTime(event.target.value)} aria-describedby="rtHelpBlock" />
                            <Form.Text id="rtHelpBlock" muted>Currently {timeDialogRace && timeDialogRace.raceTimeFormatted}. Must be no more than one hour before the timetable start or one hour after the timetable end.</Form.Text>
                        </Form.Group>
                    </Container>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={cancelChangeTimeDialog} disabled={timeDialogSaving}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={saveChangeTimeDialog} disabled={timeDialogSaving}>
                        {timeDialogSaving ? <Spinner size="sm" animation="border" /> : 'Save Time'}
                    </Button>
                </Modal.Footer>
            </Modal>
            <div className='container-fluid'>
                <div className='row'>
                    <div className='col-12 col-md-10 p-1'>
                        <h1 id="tabelLabel" className="noprint">{competitionName ? `${competitionName} - Live Timetable` : `${printTitle || ''} ${displayYear} Live Timetable`.trim()}</h1>
                        <h1 className="noscreen">{printTitle} {displayYear} Timetable</h1></div>
                    <div className='d-none d-md-block col-md-2 text-end'>
                        {isAdmin && (
                            <Button variant="primary" onClick={handleExport} className="noprint">
                                Export
                            </Button>
                        )}
                    </div>
                </div>
            </div>
            {contents}
            <div className="fixed-bottom">
                <Navbar bg="light" variant="light">
                    <Container>
                        <NavbarBrand></NavbarBrand>
                        <Navbar.Text>
                            <div className='updateStatus'>
                                {status}<br />Updated: {lastUpdate}
                                {isAdmin && <><br />Connected users: {connectedUsers ?? '-'}</>}
                            </div>
                        </Navbar.Text>
                    </Container>
                </Navbar>
            </div>
            <div className="divFooter container-fluid">
                <div className='row'>
                    <div className='col-9'></div>
                    <div className='col-3'></div>
                </div>
            </div>
        </div>
    );
}
